import { useCallback, useEffect, useState } from 'react';
import type {
  ComplianceResult,
  StageState,
  UnifiedReport,
} from '../types';

interface DemoRunState {
  inspectionId: string;
  stage1: StageState<{ service: string; status: string }>;
  stage2: StageState<ComplianceResult>;
  stage3: StageState<UnifiedReport>;
  isRunning: boolean;
}

type StageKey = 'stage1' | 'stage2' | 'stage3';

/**
 * Orchestrates the three-stage demo. Each stage talks to a same-origin
 * `/api/*` endpoint (CF Pages Function) so the browser never handles the
 * upstream bearer tokens.
 *
 * Stages run sequentially — stage 2 only fires after stage 1 succeeds, and
 * stage 3 only fires after stage 2 — so a single upstream outage surfaces
 * once instead of propagating through all three cards with misleading
 * "error" badges.
 */
export function useDemoRun() {
  const [state, setState] = useState<DemoRunState>(() => ({
    inspectionId: 'insp_test_ts_01',
    stage1: { status: 'idle' },
    stage2: { status: 'idle' },
    stage3: { status: 'idle' },
    isRunning: false,
  }));

  // Fetch the inspection ID from the server on mount so the demo target
  // can be reconfigured via wrangler env without a redeploy of the SPA.
  useEffect(() => {
    fetch('/api/config').then(r => r.ok ? r.json() : null).then((data) => {
      if (data?.inspectionId && typeof data.inspectionId === 'string') {
        setState((s) => ({ ...s, inspectionId: data.inspectionId }));
      }
    }).catch(() => { /* fall back to default */ });
  }, []);

  const patch = useCallback(
    <K extends StageKey>(key: K, next: DemoRunState[K]) =>
      setState((s) => ({ ...s, [key]: next })),
    [],
  );

  const run = useCallback(async () => {
    setState((s) => ({
      ...s,
      isRunning: true,
      stage1: { status: 'running' },
      stage2: { status: 'idle' },
      stage3: { status: 'idle' },
    }));

    // --- Stage 1: Aviation RAG health ---
    try {
      const res = await fetch('/api/compliance-check', { method: 'GET' });
      if (!res.ok) throw new Error(`health ${res.status}`);
      const data = await res.json() as { service: string; status: string };
      patch('stage1', { status: 'ok', data });
    } catch (err) {
      patch('stage1', { status: 'error', error: (err as Error).message });
      setState((s) => ({ ...s, isRunning: false }));
      return;
    }

    // --- Stage 2: Direct compliance-check ---
    patch('stage2', { status: 'running' });
    try {
      const res = await fetch('/api/compliance-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          component: 'airframe',
          observation: 'approved data current for rating',
          state: 'GA',
          country: 'US',
          jurisdiction: 'faa-part145',
        }),
      });
      if (!res.ok) throw new Error(`compliance-check ${res.status}`);
      const data = await res.json() as ComplianceResult;
      patch('stage2', { status: 'ok', data });
    } catch (err) {
      patch('stage2', { status: 'error', error: (err as Error).message });
      setState((s) => ({ ...s, isRunning: false }));
      return;
    }

    // --- Stage 3: Full report compile + fetch ---
    patch('stage3', { status: 'running' });
    try {
      // Use the current inspection ID from state. React's setState batching
      // means the `state.inspectionId` captured by the closure at run()
      // invocation time is stale only if the user clicked run before the
      // config fetch resolved — fall back to the default either way.
      const inspectionId = state.inspectionId;
      const compileRes = await fetch(`/api/report/${encodeURIComponent(inspectionId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'final', force: true }),
      });
      if (compileRes.status !== 200 && compileRes.status !== 201) {
        throw new Error(`compile ${compileRes.status}`);
      }
      const jsonRes = await fetch(`/api/report/${encodeURIComponent(inspectionId)}`);
      if (!jsonRes.ok) throw new Error(`report-json ${jsonRes.status}`);
      const data = await jsonRes.json() as UnifiedReport;
      patch('stage3', { status: 'ok', data });
    } catch (err) {
      patch('stage3', { status: 'error', error: (err as Error).message });
    } finally {
      setState((s) => ({ ...s, isRunning: false }));
    }
  }, [patch, state.inspectionId]);

  return { ...state, run };
}
